import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(
    () => articles.find((a) => a.id === Number(id)),
    [id],
  );

  const relatedArticles = useMemo(
    () => {
      if (!article) return [];
      return articles.filter((a) => a.id !== article.id).slice(0, 3);
    },
    [article],
  );

  if (!article) {
    return (
      <>
        <SEO
          title="文章未找到 - 黄瓜视频网页版"
          description="抱歉，您查找的文章不存在或已被移除。"
        />
        <div className="pt-24 md:pt-28">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 mb-4">
              <i className="ri-article-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      </>
    );
  }

  const pageTitle = `${article.title} - 黄瓜视频网页版`;
  const canonicalUrl = `https://www.huanggua-video.com/article/${article.id}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黄瓜视频网页版',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.huanggua-video.com' },
        { '@type': 'ListItem', position: 2, name: '文章', item: 'https://www.huanggua-video.com/article/1' },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.coverImage}
        schema={articleSchema}
      />

      <main>
        <section className="pt-24 md:pt-28 pb-6 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line mx-2"></i>
                <Link to="/article/1" className="hover:text-primary-600 transition-colors cursor-pointer">文章</Link>
                <i className="ri-arrow-right-s-line mx-2"></i>
                <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
              </nav>
            </div>
          </div>
        </section>

        <article className="w-full px-4 md:px-6 py-4 pb-12 md:pb-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                <span className="text-xs text-foreground-400">· {article.readTime}阅读</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-sm text-primary-600"></i>
                </div>
                <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
              </div>
            </div>

            <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom text-foreground-800 leading-relaxed">
              {article.content.split('\n').map((paragraph, idx) => {
                const trimmed = paragraph.trim();
                if (!trimmed) return null;

                if (trimmed.startsWith('## ')) {
                  return (
                    <h2 key={idx} className="font-heading text-lg md:text-xl font-bold text-foreground-900 mt-8 mb-4">
                      {trimmed.replace('## ', '')}
                    </h2>
                  );
                }

                if (trimmed.startsWith('- ')) {
                  return (
                    <li key={idx} className="text-sm md:text-base text-foreground-700 ml-4 mb-1.5 list-disc">
                      {trimmed.replace('- ', '')}
                    </li>
                  );
                }

                return (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 mb-4 leading-relaxed">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-6">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={related.coverImage}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{related.excerpt}</p>
                    <div className="flex items-center gap-2 mt-3 text-xs text-foreground-400">
                      <span>{related.publishedAt}</span>
                      <span>· {related.readTime}阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}